import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { getSession } from '../../getSession';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import dialerService from './services/dialerService';
import { useDialerTimer } from './hooks/useDialerTimer';
import { useMqttDialer } from './hooks/useMqttDialer';

// Components
import CallListSidebarV2 from './components/CallListSidebarV2';
import CallListHeaderV2 from './components/CallListHeaderV2';
import CallListTableV2 from './components/CallListTableV2';
import CallWidgetV2 from './components/CallWidgetV2';
import AfterCallWorkModalV2 from './components/AfterCallWorkModalV2';
import CallPreferenceModalV2 from './components/CallPreferenceModalV2';
import RescheduleModalV2 from './components/RescheduleModalV2';
import MoveToListModalV2 from './components/MoveToListModalV2';
import FollowupModalV2 from './components/FollowupModalV2';
import EmailModalV2 from './components/EmailModalV2';
import SMSModalV2 from './components/SMSModalV2';
import TaskModalV2 from './components/TaskModalV2';
import AppointmentModalV2 from './components/AppointmentModalV2';
import NoteModalV2 from './components/NoteModalV2';
import DocumentModalV2 from './components/DocumentModalV2';
import WebFormModalV2 from './components/WebFormModalV2';
import VoiceModalV2 from './components/VoiceModalV2';
import DealModalV2 from './components/DealModalV2';

const CallListModuleV2 = () => {
  const [leftTabs, setLeftTabs] = useState([]);

  // ── Module Permissions (PAG10381 = Call List) ─────────────────────────────
  const { permissions, canAdd, canEdit, canDelete, canSearch, canView, PermissionDeniedComponent } = useModulePermissions('PAG10381');

  const [activeTab, setActiveTab] = useState('allContacts');
  const [gridData, setGridData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [debouncedFilterText, setDebouncedFilterText] = useState('');
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [isCallActive, setIsCallActive] = useState(false);
  const [activeCallInfo, setActiveCallInfo] = useState(null);
  const [activeUniqueId, setActiveUniqueId] = useState(null);
  const [activeScheduleCallId, setActiveScheduleCallId] = useState(null);
  const [isAcwModalOpen, setIsAcwModalOpen] = useState(false);
  const [isPreferenceModalOpen, setIsPreferenceModalOpen] = useState(false);
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [isMoveOpen, setIsMoveOpen] = useState(false);
  const [activeActionModal, setActiveActionModal] = useState(null); // 'addFollowUp', 'email', 'sms', etc.
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [tableConfig, setTableConfig] = useState([]);
  const [fullSettings, setFullSettings] = useState({});
  const [hierarchyUsers, setHierarchyUsers] = useState([]);

  const {
    callStatus,
    callDuration,
    setCallStatus
  } = useMqttDialer(activeUniqueId);

  const {
    times,
    timerMode,
    setTimerMode,
    activeSubMode,
    setActiveSubMode,
    isTimerRunning,
    startTimer,
    stopTimer,
    syncData
  } = useDialerTimer();

  // Handle search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilterText(filterText);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterText]);

  // Reset to page 1 on filter/date changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedFilterText, dateRange]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await dialerService.getHierarchyUsers();
        console.log('Hierarchy Users Raw Response:', response);

        let userArray = [];
        if (Array.isArray(response)) {
          userArray = response;
        } else if (response && Array.isArray(response.Details)) {
          userArray = response.Details;
        } else if (response && Array.isArray(response.data)) {
          userArray = response.data;
        } else if (response && typeof response === 'object') {
          // Look for any property that is an array
          const possibleArray = Object.values(response).find(val => Array.isArray(val));
          if (possibleArray) userArray = possibleArray;
        }

        if (userArray.length > 0) {
          const mapped = userArray
            .filter(u => u && (u.Code || u.Value || u.Id || u.UserId || u.UserID || u.value))
            .map(u => ({
              label: u.Text || u.Name || u.UserName || u.label || u.text || 'Unknown User',
              value: String(u.Code || u.Value || u.Id || u.UserId || u.UserID || u.value)
            }));

          const unique = Array.from(new Map(mapped.map(item => [item.value, item])).values());
          console.log('Final Mapped Users:', unique);
          setHierarchyUsers(unique);
        }
      } catch (error) {
        console.error('Failed to fetch hierarchy users:', error);
      }
    };
    fetchUsers();
  }, []);

  const fetchGridData = useCallback(async () => {
    setIsLoading(true);
    try {
      const datePattern = /^\d{1,2}-[a-zA-Z]{3}-\d{4}$/i; // e.g. 14-May-2026
      let effectiveFrom = dateRange.from ? dateRange.from.format('DD-MMM-YYYY') : '';
      let effectiveTo = dateRange.to ? dateRange.to.format('DD-MMM-YYYY') : '';

      // If search text looks like a date and no date range is selected, use it as date range
      if (!effectiveFrom && datePattern.test(debouncedFilterText)) {
        effectiveFrom = debouncedFilterText;
        effectiveTo = debouncedFilterText;
      }

      const params = {
        Page: currentPage,
        Rows: pageSize,
        FromDate: effectiveFrom,
        ToDate: effectiveTo,
        Contain: debouncedFilterText,
        FilterOptions: JSON.stringify({
          filterMode: activeTab,
          SearchText: debouncedFilterText,
          filterConditions: [],
          dateFilter: effectiveFrom && effectiveTo ? {
            FromDate: effectiveFrom,
            ToDate: effectiveTo,
            StartDate: effectiveFrom,
            EndDate: effectiveTo,
            startDate: effectiveFrom,
            endDate: effectiveTo
          } : null
        })
      };
      const response = await dialerService.getScheduleCallDataForGrid(params);
      if (response && response.Details) {
        setGridData(response.Details.data || []);
        setTotalCount(response.Details.itemsCount || 0);
      }
    } catch (error) {
      console.error('Failed to fetch grid data:', error);
      toast.error('Failed to load call list data');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, activeTab, debouncedFilterText, dateRange]);

  const fetchSettings = useCallback(async () => {
    try {
      const settings = await dialerService.getUserDialerSettings();
      if (settings) {
        setFullSettings(settings);
        if (settings.tableConfig && settings.tableConfig.Columns) {
          setTableConfig(settings.tableConfig.Columns);
        }
        if (settings.leftTabs && settings.leftTabs.length > 0) {
          setLeftTabs(settings.leftTabs);
          const defaultTab = settings.leftTabs.find(tab => tab.isDefault || tab.class === 'active');
          if (defaultTab) {
            setActiveTab(defaultTab.name);
          } else {
            setActiveTab(settings.leftTabs[0].name);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  }, []);

  // Load settings only once on mount
  useEffect(() => {
    fetchSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload grid data whenever tab, page, filter or date changes
  useEffect(() => {
    fetchGridData();
  }, [activeTab, currentPage, pageSize, debouncedFilterText, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    setLeftTabs(prev => prev.map(tab => ({
      ...tab,
      class: tab.name === tabName ? 'active' : ''
    })));
    setCurrentPage(1);
  };

  const handleCall = useCallback((record) => {
    if (isCallActive) return;

    setIsCallActive(true);
    const mockUniqueId = `call_${Date.now()}`;

    setActiveCallInfo({
      agentName: 'Admin', // In real case, get from session
      agentNumber: '9999999999',
      customerName: record.PersonName,
      customerNumber: record.ScheduleMobileNo,
      provider: 'Kit19 80'
    });

    setActiveUniqueId(mockUniqueId);
    setActiveScheduleCallId(record.Id);

    setTimerMode('greencall');
    setActiveSubMode('talkTime');
    startTimer();
  }, [isCallActive, startTimer, setTimerMode, setActiveSubMode]);

  // Auto-dialer logic
  useEffect(() => {
    if (isTimerRunning && activeSubMode === 'idleTime' && !isCallActive && gridData.length > 0) {
      handleCall(gridData[0]);
    }
  }, [isTimerRunning, activeSubMode, isCallActive, gridData, handleCall]);

  const handleDialerStart = () => {
    setTimerMode('greencall');
    setActiveSubMode('idleTime');
    startTimer();
  };

  const handleDialerStop = () => {
    stopTimer();
    setTimerMode('endcall');
    setActiveSubMode('dialerTime');
    setIsCallActive(false);
  };

  const handleAddTab = async (newTab) => {
    try {
      if (!newTab.name || !newTab.description) {
        throw new Error("Name and Description are required fields");
      }

      const isExist = leftTabs.some(x => x.name.toLowerCase() === newTab.name.toLowerCase());
      if (isExist) {
        throw new Error("Tab already exists");
      }

      const updatedTabs = [...leftTabs, {
        name: newTab.name,
        text: newTab.name,
        description: newTab.description,
        isDefault: newTab.isDefault,
        class: '',
        filterOptions: { filterMode: newTab.name, filterConditions: [] }
      }];

      setLeftTabs(updatedTabs);
      await dialerService.updateUserPageSetting(0, { ...fullSettings, leftTabs: updatedTabs });
      toast.success('Tab added successfully');
      return true;
    } catch (error) {
      toast.error(error.message);
      return false;
    }
  };

  const handleRemoveTab = async (index) => {
    try {
      const updatedTabs = [...leftTabs];
      updatedTabs.splice(index, 1);
      setLeftTabs(updatedTabs);
      await dialerService.updateUserPageSetting(0, { ...fullSettings, leftTabs: updatedTabs });
      toast.success('Tab removed successfully');
    } catch (error) {
      console.error('Failed to remove tab:', error);
      toast.error('Failed to remove tab');
    }
  };

  const handleReorderTabs = async (newTabs) => {
    try {
      setLeftTabs(newTabs);
      await dialerService.updateUserPageSetting(0, { ...fullSettings, leftTabs: newTabs });
      toast.success('Tab ordering updated');
    } catch (error) {
      console.error('Failed to reorder tabs:', error);
      toast.error('Failed to update tab ordering');
    }
  };


  const handleAcwSave = async (payload, isExit) => {
    try {
      await dialerService.updateDialerData('ACW', payload);
      toast.success('After Call Work saved');

      if (isExit) {
        handleDialerStop();
      } else {
        // Prepare for next call if any
        fetchGridData();
        setActiveSubMode('idleTime');
      }
    } catch (error) {
      toast.error('Failed to save After Call Work');
      throw error;
    }
  };

  const handlePreferenceSave = async (newSettings) => {
    try {
      const updatedFullSettings = {
        ...fullSettings,
        ...newSettings
      };

      // Update local state if leftTabs were changed via reordering in modal
      if (newSettings.ColumnOrder) {
        const reorderedTabs = newSettings.ColumnOrder.map(order =>
          leftTabs.find(tab => tab.name === order.name)
        ).filter(Boolean);

        if (reorderedTabs.length > 0) {
          setLeftTabs(reorderedTabs);
          updatedFullSettings.leftTabs = reorderedTabs;
        }
      }

      await dialerService.updateUserPageSetting(fullSettings.Id || 0, updatedFullSettings);
      setFullSettings(updatedFullSettings);
      setIsPreferenceModalOpen(false);
      toast.success('Settings saved successfully');
      fetchGridData(); // Refresh grid in case order affects queue
    } catch (error) {
      console.error('Failed to save preferences:', error);
      toast.error('Failed to save settings');
    }
  };

  const handleRescheduleCall = async (newTime) => {
    try {
      await dialerService.updateDialerData('UPDATE_SCHEDULE', {
        ScheduleId: selectedRecord.Id,
        ScheduleTime: newTime
      });
      toast.success('Call Re-Scheduled successfully');
      setIsRescheduleOpen(false);
      fetchGridData();
    } catch (error) {
      console.error('Failed to reschedule:', error);
      toast.error('Failed to reschedule call');
    }
  };

  const handleRemoveSchedule = async (record) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#94a3b8",
      confirmButtonText: "Yes, delete it!"
    });

    if (result.isConfirmed) {
      try {
        await dialerService.updateDialerData('REMOVE_RECORD', {
          ScheduleId: record.Id
        });
        toast.success('Call Removed successfully');
        fetchGridData();
      } catch (error) {
        console.error('Failed to remove:', error);
        toast.error('Failed to remove call');
      }
    }
  };

  const handleMoveToList = async (destinationTab) => {
    try {
      await dialerService.updateDialerData('MOVE_TO_OTHER_LIST', {
        ScheduleId: selectedRecord.Id,
        LabelName: destinationTab
      });
      toast.success(`Schedule call moved to ${destinationTab} list`);
      setIsMoveOpen(false);
      fetchGridData();
    } catch (error) {
      console.error('Failed to move:', error);
      toast.error('Failed to move call');
    }
  };

  const handleEntityAction = (actionKey, record) => {
    // Validate role permissions first
    if (permissions) {
      if (actionKey === 'addFollowUp' && permissions.AddFollowup === false) {
        toast.error("You do not have permission to add followup");
        return;
      }
      if (actionKey === 'email' && permissions.SendMail === false) {
        toast.error("You do not have permission to send email");
        return;
      }
      if (actionKey === 'sms' && permissions.SendSms === false) {
        toast.error("You do not have permission to send SMS");
        return;
      }
      if (actionKey === 'voice' && permissions.SendVoice === false) {
        toast.error("You do not have permission to send Voice");
        return;
      }
      if (actionKey === 'addNote' && permissions.AddNote === false) {
        toast.error("You do not have permission to add notes");
        return;
      }
      if (actionKey === 'uploadDocument' && permissions.UploadDocument === false) {
        toast.error("You do not have permission to upload document");
        return;
      }
      if (actionKey === 'addTask' && permissions.AddTask === false) {
        toast.error("You do not have permission to add task");
        return;
      }
      if (actionKey === 'addAppointment' && permissions.AddAppointment === false) {
        toast.error("You do not have permission to add appointment");
        return;
      }
      if (actionKey === 'addOpportunity' && permissions.AddDeal === false) {
        toast.error("You do not have permission to add deal");
        return;
      }
      if (actionKey === 'addWebForm' && permissions.AddWebform === false) {
        toast.error("You do not have permission to fill webform");
        return;
      }
    }
    console.log(`Action triggered: ${actionKey} for ${record.PersonName}`);
    setSelectedRecord(record);
    setActiveActionModal(actionKey);
  };

  const handleFollowupSave = async (payload) => {
    try {
      console.log('Saving followup:', payload);
      toast.success('Follow-up added successfully');
      setActiveActionModal(null);
    } catch (error) {
      console.error('Failed to save followup:', error);
      toast.error('Failed to save follow-up');
    }
  };

  const handleActionModalClose = () => {
    setActiveActionModal(null);
  };

  if (!canView) {
    return (
      <div>
        <input type="hidden" id="hdnModuleCode" name="hdnModuleCode" value="PAG10381" readOnly />
        <PermissionDeniedComponent />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[#f8fafc] overflow-hidden font-['Inter', 'Roboto', 'sans-serif']">
      <input type="hidden" id="hdnModuleCode" name="hdnModuleCode" value="PAG10381" readOnly />
      <Toaster position="top-right" />

      {/* Sidebar */}
      <CallListSidebarV2
        tabs={leftTabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onAddTab={handleAddTab}
        onRemoveTab={handleRemoveTab}
        onReorderTabs={handleReorderTabs}
        onOpenPreferences={() => setIsPreferenceModalOpen(true)}
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        canAdd={canAdd}
        canEdit={canEdit}
        canDelete={canDelete}
      />

      {/* Main Content Container */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Premium Header with Timers */}
        <CallListHeaderV2
          times={times}
          timerMode={timerMode}
          onTimerStart={handleDialerStart}
          onTimerStop={handleDialerStop}
          isTimerRunning={isTimerRunning}
        />

        {/* Filters & Table Section */}
        <div className="flex-1 overflow-auto p-6 space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <CallListTableV2
              data={gridData}
              isLoading={isLoading}
              totalCount={totalCount}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              onCall={handleCall}
              onReschedule={(record) => {
                setSelectedRecord(record);
                setIsRescheduleOpen(true);
              }}
              onRemove={handleRemoveSchedule}
              onMove={(record) => {
                setSelectedRecord(record);
                setIsMoveOpen(true);
              }}
              onActionClick={handleEntityAction}
              onSearch={setFilterText}
              onDateChange={(dates) => setDateRange({ from: dates ? dates[0] : null, to: dates ? dates[1] : null })}
              customColumns={tableConfig}
              canAdd={canAdd}
              canEdit={canEdit}
              canDelete={canDelete}
              canSearch={canSearch}
              rolePermissions={permissions}
            />
          </div>
        </div>
      </div>

      {/* Call Widget */}
      {activeUniqueId && activeCallInfo && (
        <CallWidgetV2
          callInfo={activeCallInfo}
          status={callStatus}
          duration={callDuration}
          onClose={() => {
            setActiveUniqueId(null);
            setIsCallActive(false);
          }}
          onReport={() => {
            setIsAcwModalOpen(true);
          }}
        />
      )}

      <AfterCallWorkModalV2
        isOpen={isAcwModalOpen}
        onClose={() => setIsAcwModalOpen(false)}
        onSave={handleAcwSave}
        activeCall={{
          scheduleCallId: activeScheduleCallId,
          uniqueId: activeUniqueId
        }}
        upcomingCalls={gridData}
      />

      <CallPreferenceModalV2
        isOpen={isPreferenceModalOpen}
        onClose={() => setIsPreferenceModalOpen(false)}
        onSave={handlePreferenceSave}
        settings={fullSettings}
      />

      <RescheduleModalV2
        isOpen={isRescheduleOpen}
        onClose={() => setIsRescheduleOpen(false)}
        onSave={handleRescheduleCall}
        initialTime={selectedRecord?.ScheduleTime}
        recordName={selectedRecord?.PersonName || selectedRecord?.ScheduleMobileNo}
      />

      <MoveToListModalV2
        isOpen={isMoveOpen}
        onClose={() => setIsMoveOpen(false)}
        onSave={handleMoveToList}
        tabs={leftTabs}
        recordName={selectedRecord?.PersonName || selectedRecord?.ScheduleMobileNo}
      />

      <FollowupModalV2
        isOpen={activeActionModal === 'addFollowUp'}
        onClose={handleActionModalClose}
        onSave={handleFollowupSave}
        record={selectedRecord}
        users={hierarchyUsers}
      />

      <EmailModalV2
        isOpen={activeActionModal === 'email'}
        onClose={handleActionModalClose}
        record={selectedRecord}
      />

      <SMSModalV2
        isOpen={activeActionModal === 'sms'}
        onClose={handleActionModalClose}
        record={selectedRecord}
      />

      <TaskModalV2
        isOpen={activeActionModal === 'addTask'}
        onClose={handleActionModalClose}
        record={selectedRecord}
        users={hierarchyUsers}
      />

      <AppointmentModalV2
        isOpen={activeActionModal === 'addAppointment'}
        onClose={handleActionModalClose}
        record={selectedRecord}
        users={hierarchyUsers}
      />

      <NoteModalV2
        isOpen={activeActionModal === 'addNote'}
        onClose={handleActionModalClose}
        record={selectedRecord}
      />

      <DocumentModalV2
        isOpen={activeActionModal === 'uploadDocument'}
        onClose={handleActionModalClose}
        record={selectedRecord}
      />

      <WebFormModalV2
        isOpen={activeActionModal === 'addWebForm'}
        onClose={handleActionModalClose}
        record={selectedRecord}
      />

      <VoiceModalV2
        isOpen={activeActionModal === 'voice'}
        onClose={handleActionModalClose}
        record={selectedRecord}
      />

      <DealModalV2
        isOpen={activeActionModal === 'addOpportunity'}
        onClose={handleActionModalClose}
        record={selectedRecord}
      />
    </div>
  );
};

export default CallListModuleV2;
